import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button/Button';
import './ListePiecesCard.css';

// Carte "Liste des pièces éventuelles composant la documentation technique", rendue comme derniere carte de la
// grille des documents (prop `trailingCard` de DocumentsList).
//
// Le texte est TRONQUE, jamais scrollable : la carte tient une place de document dans la grille, et
// un scroll interne sur une boite de cette taille est invisible (rien n'indique qu'il reste du
// contenu) et se dispute la molette avec la page. Au-dela de la troncature, "Lire la suite" ouvre le
// texte entier dans une modale.
//
// Partagee par les DEUX vues du BC (acheteur SuiviOperationBCV2, prestataire SuiviDeCommandeBCV2) :
// elles rendaient jusqu'ici la meme carte de deux facons — styles inline d'un cote, classes
// `.bcc-desc__docs-champ*` de l'autre. Une modale ajoutee deux fois aurait diverge a la premiere
// retouche.

interface ListePiecesCardProps {
  /** Texte libre saisi par l'acheteur. Vide/absent -> la carte affiche le placeholder. */
  texte?: string | null;
  /** Titre de la carte. Par defaut l'intitule du champ. */
  label?: string;
  placeholder?: string;
}

const ListePiecesCard: React.FC<ListePiecesCardProps> = ({
  texte,
  label = 'Liste des pièces éventuelles composant la documentation technique',
  placeholder = '-',
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [tronque, setTronque] = useState(false);
  const texteRef = useRef<HTMLDivElement | null>(null);

  const contenu = (texte || '').trim();
  const vide = contenu.length === 0;

  // "Lire la suite" n'apparait que s'il reste vraiment du texte. On le MESURE (`scrollHeight` >
  // `clientHeight` sous le clamp CSS) plutot que de le deviner a un nombre de caracteres : le meme
  // texte tient sur deux lignes ou sur cinq selon la largeur de la carte et ses retours a la ligne.
  useLayoutEffect(() => {
    const el = texteRef.current;
    if (!el) return undefined;

    const mesurer = () => setTronque(el.scrollHeight > el.clientHeight + 1); // +1 : arrondis sub-pixel
    mesurer();

    // La carte est dans une grille responsive : sa largeur change sans que le texte change. Sans
    // observer, le lien resterait sur l'etat de la premiere mesure.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(mesurer);
    observer.observe(el);
    return () => observer.disconnect();
  }, [contenu]);

  // Echap ferme la modale : une modale ouverte ne doit pas rester coincee.
  useEffect(() => {
    if (!ouvert) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ouvert]);

  return (
    <div className="lpc">
      <span className="lpc__label">{label}</span>

      <div className="lpc__texte" ref={texteRef}>
        {vide ? placeholder : contenu}
      </div>

      {tronque && !vide && (
        <button type="button" className="lpc__lien" onClick={() => setOuvert(true)}>
          Lire la suite
        </button>
      )}

      {/* Portail sur `body` : la carte vit dans une grille aux ancetres `overflow: hidden`
          (`.section-pliable`, `.documents-grid-trailing`) — rendue sur place, la modale y serait
          rognee. */}
      {ouvert &&
        createPortal(
          <div className="lpc-modal-overlay" onClick={() => setOuvert(false)}>
            <div
              className="lpc-modal"
              role="dialog"
              aria-modal="true"
              aria-label={label}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lpc-modal__bar">
                <span className="lpc-modal__titre">{label}</span>
                <Button value="Fermer" variant="primary" size="small" onClick={() => setOuvert(false)} />
              </div>
              {/* Le scroll est ICI, pas dans la carte : une modale pleine hauteur l'assume. */}
              <div className="lpc-modal__corps">{contenu}</div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ListePiecesCard;
